import {
  ListItem,
  DcuplFilterOptions,
  InverseIndexReferenceMap,
  DcuplGlobalQueryOptions,
  DcuplQueryGroup,
  DcuplFacet,
} from './types';

import { QueryManager } from './queryData';
import { DcuplQueryBuilder } from './query-builder';
import { cloneDeep } from 'lodash-es';

const queryBuilder = new DcuplQueryBuilder();

export function getFacets<ListType extends ListItem = ListItem>(
  modelData: Map<string, ListType>,
  remoteModelData: Map<string, ListType>,
  options: DcuplFilterOptions,
  query: DcuplGlobalQueryOptions<any>,
  attribute: string,
  modelIndexMap: InverseIndexReferenceMap,
  queryManager: QueryManager,
): DcuplFacet[] {
  const filterItemEntries: DcuplFacet[] = [];

  const queryWithoutCurrentFilter = queryBuilder.removeQuery({ groupKey: attribute }, cloneDeep(query));

  const currentFilteredData = queryManager.queryData(
    modelData,
    queryWithoutCurrentFilter as unknown as DcuplQueryGroup,
    false,
    modelIndexMap,
  );

  const remoteModelDataKeys = remoteModelData ? [...remoteModelData?.keys()] : [];
  const validFacetKeys: Set<any> = new Set(remoteModelDataKeys);

  const indexMap = modelIndexMap.get(attribute)!;

  if (!options.excludeUnresolved) {
    for (const key of indexMap!.keys()) {
      validFacetKeys.add(key);
    }
  }

  if (options.excludeUndefineds) {
    validFacetKeys.delete(undefined);
  } else {
    validFacetKeys.add(undefined);
  }

  for (const validFacetKey of validFacetKeys.values()) {
    const result = new Map<string, ListType>();

    const indexedValueKeys = indexMap.get(validFacetKey) || [];

    for (const key of indexedValueKeys) {
      const value = currentFilteredData.get(key);
      if (value) {
        result.set(key, value);
        if (!options?.calculateFacets) {
          break;
        }
      }
    }

    const facetEntry: DcuplFacet = {
      key: String(validFacetKey),
      value: validFacetKey,
      selected: isFilterItemEntrySelected(attribute, validFacetKey, query),
      enabled: result.size > 0,
      size: result.size,
      entry: remoteModelData.get(validFacetKey)!,
      resultKeys: Array.from(result.keys()),
    };

    if (options?.excludeZeros && facetEntry.size === 0) {
      continue;
    }

    if (options?.count && filterItemEntries.length === options?.count) {
      return filterItemEntries;
    }

    filterItemEntries.push(facetEntry);
  }

  return filterItemEntries;
}

const isFilterItemEntrySelected = (
  groupKey: string,
  facetKey: string,
  query: DcuplGlobalQueryOptions<any>,
): boolean => {
  const idx = query.queries.findIndex((query) => queryBuilder.isQueryGroup(query) && query.groupKey === groupKey);

  if (idx > -1) {
    const queryGroup = query.queries[idx] as DcuplQueryGroup;

    return !!queryGroup.queries.find((query) => {
      if (queryBuilder.isQuery(query)) {
        const key = query.value?.key || query.value;
        return query.attribute === groupKey && key === facetKey;
      }
      return false;
    });
  }
  return false;
};
